import cookies from 'js-cookie';

import { getIsStorageAvailable } from '@vestico/utils';

import { getResolvedLocale } from '@vestico/i18n';

import { defaultCookieAttributes } from '../helpers';
import { BraSizeSourceID } from '../userFields';
import { EyeColorModel } from '../userFields/eyeModel';
import { HairToneModel } from '../userFields/hairModel';
import { HipShapeModel, ShoulderShapeModel, TummyShapeModel } from '../userFields/shapeOptions';
import { SkinSensitivityModel, SkinToneModel, SkinTypeModel, SkinUndertoneModel } from '../userFields/skinModel';
import {
  SupportedMatchInfoHasHashCategoryMapping,
  SupportedMatchInfoHashCategories,
  SupportedMatchInfoHashCategoryMapping,
} from '../userMatchInfoHash';
import { SupportedMatchInfoHashCategory } from '../userMatchInfoHash';
import { Gender } from './constants';

export type UserDocumentID = string;

export enum WeightUnit {
  kilogram = 0,
  pound = 1,
  stone = 2,
}

export enum HeightUnit {
  centimeter = 0,
  feet = 1,
}

export enum AgeUnit {
  default = 0,
}

export type UnitType = WeightUnit | HeightUnit | AgeUnit;

export type InstagramUserInfo = {
  uid: string;
  username: string;
  expiry: number;
};

export type TikTokUserInfo = {
  username: string;
};

export const CreatorStatii = ['review', 'live', 'rejected'] as const;
export type CreatorStatus = (typeof CreatorStatii)[number];

export type Address = {
  city: string;
};

export type BodyInfo = {
  height?: number;
  weight?: number;
  braSizeBand?: number;
  braSizeCup?: number;
  hipShape?: HipShapeModel;
  shoulderShape?: ShoulderShapeModel;
  tummyShape?: TummyShapeModel;
  heightUnit: HeightUnit;
  weightUnit: WeightUnit;
  braSizeSourceId?: BraSizeSourceID;
};

export type SkinInfo = {
  skinTone?: SkinToneModel;
  skinUndertone?: SkinUndertoneModel;
  skinType?: SkinTypeModel;
  skinSensitivity?: SkinSensitivityModel;
  hairTone?: HairToneModel;
  eyeColor?: EyeColorModel;
};

export type BaseUserModel = {
  uid?: string;
  version?: number;
  gender?: Gender;
  displayName?: string;
  username?: string;
  bodyInfoHashes?: SupportedMatchInfoHashCategoryMapping;
  hasBodyInfos?: SupportedMatchInfoHasHashCategoryMapping;
  hasAnyBodyInfos?: boolean;
  signUpTimestamp?: Date;
  instagram?: InstagramUserInfo;
  tiktok?: TikTokUserInfo;
  creatorStatus?: CreatorStatus;
  address?: Address;
  birthyear?: number;
};

export type UserModelProps = {
  fallbackUsername?: string;
};
export type StorageUserModelProps = {
  // skipcq JS-0323
  [x: string]: unknown;
};

export type UserModel = BaseUserModel & UserModelProps & BodyInfo & SkinInfo;
export type StorageUserModel = BaseUserModel & StorageUserModelProps & BodyInfo & SkinInfo;

const userStorageKeyDeprecated = '@vestico/model/info';
const userStorageKey = 'co.vestico.model.info';

const currentVersion = 2.1;

export const getDefaultHeightUnit = (i18nLanguage?: string) => {
  const FT_COUNTRIES = new Set(['US', 'LR', 'MM', 'GB']);
  const browserCountryCode = getResolvedLocale(i18nLanguage, { showCountryCode: true });
  return !!browserCountryCode && FT_COUNTRIES.has(browserCountryCode.toUpperCase()) ? HeightUnit.feet : HeightUnit.centimeter;
};

export const getDefaultWeightUnit = (i18nLanguage?: string) => {
  const LBS_COUNTRIES = new Set(['US', 'LR', 'MM']);
  const browserCountryCode = getResolvedLocale(i18nLanguage, { showCountryCode: true });
  return !!browserCountryCode && LBS_COUNTRIES.has(browserCountryCode.toUpperCase()) ? WeightUnit.pound : WeightUnit.kilogram;
};

export const getBirthyearFromAge = (age?: number) => {
  if (!age) {
    return undefined;
  }
  return new Date().getFullYear() - age;
};

export const getAgeFromBirthyear = (birthyear?: number) => {
  if (!birthyear) {
    return undefined;
  }
  return new Date().getFullYear() - birthyear;
};

export const writeUserToStorage = (user: UserModel) => {
  const serializedUser = JSON.stringify(user);
  if (!getIsStorageAvailable()) {
    const attributes = defaultCookieAttributes();
    attributes.expires = 365;
    cookies.set(userStorageKey, serializedUser, attributes);
    return;
  }
  localStorage.setItem(userStorageKey, serializedUser);
};

const migrateAgeToBirthyearIfNeeded = (user: UserModel) => {
  const birthyearLocalVersion = 2.1;
  if (user.version && user.version < birthyearLocalVersion) {
    const oldModel = user as unknown as Record<string, number>;
    const oldAge = oldModel.age;

    if (oldAge) {
      user.birthyear = getBirthyearFromAge(oldAge);
    }
    delete oldModel.age;
    user.version = birthyearLocalVersion;
    writeUserToStorage(user);
  }
  return true;
};

export const userDataNeedsReset = (user: UserModel): boolean => {
  const outOfDate = user.version !== undefined && user.version < currentVersion;
  if (outOfDate) {
    const migrated = migrateAgeToBirthyearIfNeeded(user);
    if (migrated) {
      return false;
    }
  }
  return outOfDate;
};

const defaultUser = (): UserModel => {
  return {
    version: currentVersion,
    gender: Gender.female,
    heightUnit: getDefaultHeightUnit(),
    weightUnit: getDefaultWeightUnit(),
  };
};

export const resetUser = (oldUser?: UserModel | null): UserModel => {
  const user = defaultUser();

  if (oldUser) {
    user.heightUnit = oldUser.heightUnit;
    user.weightUnit = oldUser.weightUnit;
  }

  writeUserToStorage(user);
  return user;
};

export const getUserFromStorage = (): UserModel => {
  let userModelString: string | undefined | null;
  if (getIsStorageAvailable()) {
    userModelString = localStorage.getItem(userStorageKey);

    // Fall back to deprecated key
    if (!userModelString) {
      userModelString = localStorage.getItem(userStorageKeyDeprecated);
    }
  } else {
    userModelString = cookies.get(userStorageKey);
  }

  console.debug(`loading user ${userModelString}`);

  if (userModelString) {
    const user = JSON.parse(userModelString);
    if (userDataNeedsReset(user)) {
      console.debug('resetting to default user');
      resetUser();
      return defaultUser();
    }
    return user;
  }

  console.debug('loading default user');
  return defaultUser();
};

export const updateUserDefaultUnitsIfNeeded = (user?: UserModel | null, i18nLanguage?: string) => {
  if (!user) {
    return;
  }

  if (!user.height) {
    user.heightUnit = getDefaultHeightUnit(i18nLanguage);
  }
  if (!user.weight) {
    user.weightUnit = getDefaultWeightUnit(i18nLanguage);
  }
};

const userDataCompletedForFashion = (user: UserModel) => {
  const { gender, height, weight, tummyShape, braSizeBand, braSizeCup, hipShape, shoulderShape, birthyear } = user;
  const standardPropertiesValid =
    gender !== null &&
    gender !== undefined &&
    birthyear !== null &&
    birthyear !== undefined &&
    height !== null &&
    height !== undefined &&
    weight !== null &&
    weight !== undefined &&
    tummyShape !== null &&
    tummyShape !== undefined;
  const femalePropertiesValid =
    gender === Gender.female &&
    braSizeBand !== null &&
    braSizeBand !== undefined &&
    braSizeCup !== null &&
    braSizeCup !== undefined &&
    hipShape !== null &&
    hipShape !== undefined;
  const malePropertiesValid = gender === Gender.male && shoulderShape !== null && shoulderShape !== undefined;

  return standardPropertiesValid && (femalePropertiesValid || malePropertiesValid);
};

const userDataIsMatchableForBeauty = (user: UserModel) => {
  const { skinTone, skinType, skinUndertone, hairTone, birthyear } = user;
  const completed = !!skinTone && !!skinType && !!skinUndertone && !!hairTone && !!birthyear;
  return completed;
};

const userDataIsMatchableForSkincare = (user: UserModel) => {
  const { skinType, skinSensitivity, birthyear } = user;
  const completed = !!skinType && !!birthyear && !!skinSensitivity;
  return completed;
};

const userDataCompletedForCategory = (user: UserModel, category: SupportedMatchInfoHashCategory, requiresUID = true, checkReset = false) => {
  const { uid } = user;

  const uidValid = !requiresUID || (uid !== null && uid !== undefined);

  const categorySpecificQuestions: Record<SupportedMatchInfoHashCategory, () => boolean> = {
    default: () => userDataCompletedForFashion(user),
    apparel: () => userDataCompletedForFashion(user),
    bras: () => userDataCompletedForFashion(user),
    beauty: () => userDataIsMatchableForBeauty(user),
    skincare: () => userDataIsMatchableForSkincare(user),
  };

  const didAnswerQuestions = categorySpecificQuestions[category]();
  return uidValid && didAnswerQuestions && (!checkReset || !userDataNeedsReset(user));
};

export const userDataCompleted = (user: UserModel, category?: SupportedMatchInfoHashCategory, requiresUID = true, checkReset = false) => {
  const categories = category ? [category] : SupportedMatchInfoHashCategories;
  for (const category of categories) {
    if (userDataCompletedForCategory(user, category, requiresUID, checkReset)) {
      return true;
    }
  }
  return false;
};

const userDataIsMatchableForFashion = (user: UserModel) => {
  const { gender, height, birthyear } = user;
  const requiredPropertiesValid = gender !== null && gender !== undefined && height !== null && height !== undefined;
  const femalePropertiesValid = gender === Gender.female;
  const malePropertiesValid = gender === Gender.male;
  const optionalPropertiesValid = birthyear !== null || birthyear !== undefined || typeof birthyear === 'number';
  return requiredPropertiesValid && (femalePropertiesValid || malePropertiesValid) && optionalPropertiesValid;
};

const userDataIsMatchableForCategory = (user: UserModel, category: SupportedMatchInfoHashCategory) => {
  const categorySpecificQuestions: Record<SupportedMatchInfoHashCategory, () => boolean> = {
    default: () => userDataIsMatchableForFashion(user),
    apparel: () => userDataIsMatchableForFashion(user),
    bras: () => userDataIsMatchableForFashion(user),
    beauty: () => userDataIsMatchableForBeauty(user),
    skincare: () => userDataIsMatchableForSkincare(user),
  };

  return categorySpecificQuestions[category]();
};

export const userDataIsMatchable = (user: UserModel, category?: SupportedMatchInfoHashCategory) => {
  const categories = category ? [category] : SupportedMatchInfoHashCategories;
  for (const category of categories) {
    if (userDataIsMatchableForCategory(user, category)) {
      return true;
    }
  }
  return false;
};

export const userHasEnteredAnyData = (user: UserModel) => {
  const {
    displayName,
    username,
    birthyear,
    height,
    weight,
    tummyShape,
    braSizeBand,
    braSizeCup,
    hipShape,
    shoulderShape,
    skinSensitivity,
    skinTone,
    skinType,
    skinUndertone,
    eyeColor,
    hairTone,
  } = user;

  return (
    !!displayName ||
    !!username ||
    !!birthyear ||
    !!height ||
    !!weight ||
    (tummyShape !== undefined && tummyShape !== null) ||
    !!braSizeBand ||
    !!braSizeCup ||
    (hipShape !== undefined && hipShape !== null) ||
    (shoulderShape !== undefined && shoulderShape !== null) ||
    !!skinTone ||
    !!skinType ||
    !!skinSensitivity ||
    !!skinUndertone ||
    !!eyeColor ||
    !!hairTone
  );
};
